import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { RequestTransformFunction } from 'maplibre-gl';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';
import { IVectorSource } from '../../interfaces/sjmap2/vector/IVectorSource';

@Injectable({ providedIn: 'root' })
export class MartinService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly baseUrl = environment.services.urlMartin.replace(/\/$/, '');
  private tokenCache: string | null = null;
  private tokenExpiresAtMs = 0;
  private tokenPromise: Promise<string | null> | null = null;

  readonly transformRequest: RequestTransformFunction = (url: string) => {
    if (!this.isOwnUrl(url)) return { url };
    const token = this.tokenCache;
    if (!token) return { url };
    return { url, headers: { Authorization: `Bearer ${token}` } };
  };

  isOwnUrl(url: string): boolean {
    return !!this.baseUrl && url.startsWith(this.baseUrl);
  }

  isMartinSource(source: IVectorSource): boolean {
    return source.type === 'martin';
  }

  buildTileJsonUrl(source: string): string {
    if (source.startsWith('http')) return source;
    return `${this.baseUrl}/${source.replace(/^\/+/, '')}`;
  }

  async getBounds(source: Pick<IVectorSource, 'type' | 'source' | 'source_layer'>): Promise<[number, number, number, number]> {
    const url = this.buildTileJsonUrl(source.source);
    await this.preFetchToken();
    const headers = this.tokenCache
      ? new HttpHeaders({ Authorization: `Bearer ${this.tokenCache}` })
      : new HttpHeaders();
    const tileJson = await firstValueFrom(this.http.get<{ bounds?: number[] }>(url, { headers }));
    if (!Array.isArray(tileJson.bounds) || tileJson.bounds.length !== 4) {
      throw new Error(`Martin TileJSON missing bounds: ${url}`);
    }
    return tileJson.bounds as [number, number, number, number];
  }

  async preFetchToken(): Promise<string | null> {
    // An open Explorer tab can outlive the Cognito access token. Revalidate
    // before exposing a newly published Tala PMTiles source to MapLibre.
    if (this.tokenCache && this.tokenExpiresAtMs > Date.now() + 60_000) return this.tokenCache;
    if (!this.tokenPromise) {
      this.tokenPromise = firstValueFrom(this.authService.currentSession())
        .then((session): string | null => {
          const accessToken = session?.tokens?.accessToken;
          this.tokenCache = accessToken?.toString() ?? null;
          const expiresAt = Number(accessToken?.payload?.exp);
          this.tokenExpiresAtMs = Number.isFinite(expiresAt) && expiresAt > 0
            ? expiresAt * 1000
            : Date.now() + 5 * 60_000;
          return this.tokenCache;
        })
        .catch((): string | null => {
          this.tokenCache = null;
          this.tokenExpiresAtMs = 0;
          return null;
        })
        .finally(() => { this.tokenPromise = null; });
    }
    return this.tokenPromise;
  }
}
